import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BOOK_URL, M, scrollToId, EASE } from "../lib/site";

const LINKS = [
  ["The Night", "experience"],
  ["Bottomless", "bottomless"],
  ["Guest List", "guestlist"],
  ["Join The Team", "team"],
  ["Contact", "contact"],
];

export const Nav = () => {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 1.6 }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 border-b ${solid ? "bg-[#09080B]/75 backdrop-blur-xl border-[var(--line)]" : "bg-transparent border-transparent"}`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-5 sm:px-10">
          <button onClick={() => go("top")} data-testid="nav-logo" aria-label="Back to top">
            <img src={M.logo} alt="Eleganté" className="h-9 w-auto sm:h-11" />
          </button>
          <nav className="hidden items-center gap-9 lg:flex">
            {LINKS.map(([label, id]) => (
              <button key={id} onClick={() => go(id)} data-testid={`nav-link-${id}`}
                className="group relative text-[13px] uppercase tracking-[0.2em] text-[#F8F6F0]/80 transition-colors duration-300 hover:text-[var(--glow)]">
                {label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--glow)] transition-[width] duration-500 group-hover:w-full" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-book-btn"
              className="btn-gold hidden items-center gap-2 rounded-full px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.18em] sm:inline-flex">
              Book Bottomless <ArrowUpRight size={14} />
            </a>
            <button onClick={() => setOpen(true)} className="btn-ghost rounded-full p-3 lg:hidden" data-testid="nav-menu-open" aria-label="Open menu">
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "circle(0% at 95% 4%)" }}
            animate={{ clipPath: "circle(150% at 95% 4%)" }}
            exit={{ clipPath: "circle(0% at 95% 4%)" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#0d0b10] px-6 pb-10 pt-6"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <img src={M.logo} alt="Eleganté" className="h-9" />
              <button onClick={() => setOpen(false)} className="btn-ghost rounded-full p-3" data-testid="nav-menu-close" aria-label="Close menu"><X size={18} /></button>
            </div>
            <div className="mt-14 flex flex-col gap-5">
              {LINKS.map(([label, id], i) => (
                <motion.button key={id} onClick={() => go(id)} data-testid={`mobile-link-${id}`}
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.07, ease: EASE, duration: 0.7 }}
                  className="text-left font-display text-5xl text-[#F8F6F0]">
                  {label}
                </motion.button>
              ))}
            </div>
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-book-btn"
              className="btn-gold mt-auto flex items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold uppercase tracking-[0.2em]">
              Book Bottomless <ArrowUpRight size={16} />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
